import { DeckBaseAccess, DeckMode, DeckPermissionsUserRole } from '~~/entities/deck'
import { Role } from '~~/entities/user'

export function useDeckMode() {
  const currentMode = ref<DeckMode>(DeckMode.LoadingMode)

  const { state: userState } = useUser()
  const { currentMeeting } = useMeetings()
  const { deckPermissionsState, deckHasBaseAccess } = useDeckPermissions()

  const { hasRole } = useUser()

  const hasEditPermissions = computed(() => {
    const isInternalAdmin = hasRole(Role.InternalAdmin)
    const isCompanyEditAndUserInCompany =
      deckHasBaseAccess(DeckBaseAccess.CompanyEdit) &&
      userState.value.user?.companies.some(company => company.id === deckPermissionsState.value.companyId)
    const isPublicAndCompanyAdmin =
      deckHasBaseAccess(DeckBaseAccess.Public) &&
      hasRole(Role.CompanyAdmin) &&
      userState.value.user?.companies.some(company => company.id === deckPermissionsState.value.companyId)

    if (isInternalAdmin || isCompanyEditAndUserInCompany || isPublicAndCompanyAdmin) {
      return true
    }

    if (!deckPermissionsState.value.permissions) return false

    return deckPermissionsState.value.permissions?.roles.some(
      role => role.user_id === userState.value.user?.id && role.role !== DeckPermissionsUserRole.Viewer,
    )
  })

  function evaluateDeckMode(meId: number | undefined) {
    if (currentMeeting.value?.ended_at === null) {
      const uid = userState.value.user?.id
      const isHost = uid != null && meId === uid && currentMeeting.value?.host_id === uid
      currentMode.value = isHost ? DeckMode.PresentationMode : DeckMode.ParticipantMode
      return
    }

    if (!hasEditPermissions.value) {
      currentMode.value = DeckMode.PresentationMode
      return
    }

    currentMode.value = DeckMode.EditMode
  }

  function updateMode(mode: DeckMode) {
    currentMode.value = mode
  }

  return {
    currentMode: readonly(currentMode),
    hasEditPermissions,
    updateMode,
    evaluateDeckMode,
  }
}
